import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Sparkles, X, Loader2, ArrowRight, Globe2, Facebook, Instagram, Linkedin,
  MapPin, ImageIcon, ShoppingBag, ThumbsUp, MessageCircle, Share2, Heart, Copy, Check, Mail,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const CATEGORIES = [
  "Restaurant / Cafe", "Fashion & Apparel", "Beauty & Wellness", "Real Estate",
  "Education / Coaching", "Healthcare / Clinic", "Fitness / Gym", "Tech / SaaS",
  "E-commerce", "Consulting / Agency", "Travel / Tourism", "Automobile",
  "Home Services", "Other",
];

export default function InstantTrial() {
  const [name, setName] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#7c3aed");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [kit, setKit] = useState(null);

  const generate = async () => {
    if (!name.trim() || description.trim().length < 10) {
      toast.error("Please give us a business name + at least a short description.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/trial/instant-brand`, {
        business_name: name.trim(),
        category,
        description: description.trim(),
        brand_color: color,
      });
      setKit({ ...data.kit, _input: { name, category, color }, _trialId: data.id });
      setOpen(true);
      if (typeof data.remaining_quota === "number") {
        toast.success(`Brand generated · ${data.remaining_quota} free trials left today`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate brand. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative py-20 lg:py-28 bg-gradient-to-br from-[#0c0626] via-[#1a0a3e] to-[#0c0626] overflow-hidden">
      {/* decorative blobs */}
      <div className="absolute top-10 left-10 h-72 w-72 rounded-full bg-fuchsia-500/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 h-80 w-80 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
        <div className="text-center mb-12">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-500/10 text-cyan-300 text-[11px] uppercase tracking-[0.25em] font-bold"
          >
            <Sparkles className="h-3.5 w-3.5" /> Free Instant Trial
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight"
          >
            Generate Your <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 via-fuchsia-300 to-violet-300">Brand Kit</span> in 30 Seconds
          </motion.h2>
          <p className="mt-4 text-white/60 max-w-2xl mx-auto text-base lg:text-lg">
            Tell us about your business — we&apos;ll instantly create a logo concept, website hero,
            and ready-to-post content for Facebook, Instagram, LinkedIn, X, and Google Business. No signup needed.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="glass-strong rounded-3xl border border-fuchsia-500/25 p-6 lg:p-10 max-w-4xl mx-auto shadow-2xl shadow-fuchsia-500/10"
          data-testid="instant-trial-form"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-bold">Business Name *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Spice Garden Cafe"
                data-testid="trial-business-name"
                className="mt-2 w-full bg-black/30 border-2 border-white/10 focus:border-fuchsia-500/60 outline-none rounded-xl px-5 py-3.5 text-base font-medium transition"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-bold">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="trial-category"
                className="mt-2 w-full bg-black/30 border-2 border-white/10 focus:border-fuchsia-500/60 outline-none rounded-xl px-5 py-3.5 text-base"
              >
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-bold">Brand Color</label>
              <div className="mt-2 flex items-center gap-3">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  data-testid="trial-brand-color"
                  className="h-[52px] w-[52px] rounded-xl border-2 border-white/10 bg-transparent cursor-pointer"
                />
                <input
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="flex-1 bg-black/30 border-2 border-white/10 focus:border-fuchsia-500/60 outline-none rounded-xl px-5 py-3.5 text-base font-mono uppercase"
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] uppercase tracking-[0.25em] text-white/50 font-bold">What does your business do? *</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Cozy family-run cafe specialising in authentic South-Indian breakfasts and artisanal filter coffee"
                data-testid="trial-description"
                className="mt-2 w-full bg-black/30 border-2 border-white/10 focus:border-fuchsia-500/60 outline-none rounded-xl px-5 py-3.5 text-base resize-none"
              />
            </div>
          </div>

          <button
            onClick={generate}
            disabled={loading}
            data-testid="trial-generate-button"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-base text-white bg-gradient-to-r from-fuchsia-500 via-violet-600 to-cyan-500 hover:opacity-95 hover:shadow-2xl hover:shadow-fuchsia-500/40 transition-all disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> AI is crafting your brand…
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5" /> Generate My Brand
              </>
            )}
          </button>
          <p className="mt-3 text-center text-xs text-white/40">
            5 free trials per day · No credit card · No spam ever
          </p>
        </motion.div>
      </div>

      <AnimatePresence>
        {open && kit && (
          <TrialPreviewModal kit={kit} onClose={() => setOpen(false)} />
        )}
      </AnimatePresence>
    </section>
  );
}

/* =====================================================================
 * Preview Modal
 * ===================================================================== */
const TABS = [
  { id: "brand",   label: "Logo & Brand", Icon: Sparkles },
  { id: "website", label: "Website",      Icon: Globe2 },
  { id: "facebook",label: "Facebook",     Icon: Facebook },
  { id: "instagram",label:"Instagram",    Icon: Instagram },
  { id: "linkedin",label: "LinkedIn",     Icon: Linkedin },
  { id: "x",       label: "X",            Icon: XLogo },
  { id: "gmb",     label: "Google Business", Icon: MapPin },
];

function XLogo({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M16.7 6h2L13.2 12 19.7 18h-4.6l-3.5-4.2L7.7 18H5.5l5.7-6.4L5 6h4.7l3.2 3.8z" />
    </svg>
  );
}

function TrialPreviewModal({ kit: initialKit, onClose }) {
  const [tab, setTab] = useState("brand");
  const [kit, setKit] = useState(initialKit);
  const [shareOpen, setShareOpen] = useState(false);
  const input = kit._input || {};
  const trialId = kit._trialId;
  const ctaHref = trialId
    ? `/register?ref=trial&kit_id=${trialId}&utm_source=instant_trial&utm_medium=modal_cta&utm_campaign=brand_kit`
    : "/register?ref=trial";
  const pricingHref = trialId
    ? `/pricing?ref=trial&kit_id=${trialId}&utm_source=instant_trial&utm_medium=modal_link&utm_campaign=brand_kit`
    : "/pricing?ref=trial";
  const onLogoGenerated = (b64) => setKit((k) => ({ ...k, logo_image_b64: b64 }));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[60] grid place-items-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="trial-modal"
    >
      <motion.div
        initial={{ scale: 0.95, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 16 }}
        transition={{ duration: 0.25 }}
        className="relative w-full max-w-5xl max-h-[92vh] bg-[#0c0626] border border-fuchsia-500/30 rounded-3xl flex flex-col overflow-hidden shadow-2xl shadow-fuchsia-500/30"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between gap-4 px-6 py-4 border-b border-white/10 bg-gradient-to-r from-fuchsia-500/10 to-cyan-500/10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-300 font-bold">Your Free Trial Brand Kit</div>
            <h3 className="font-heading text-xl lg:text-2xl font-black mt-0.5 truncate">{input.name}</h3>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setShareOpen(true)}
              data-testid="trial-share-button"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-cyan-500/20 to-fuchsia-500/20 border border-cyan-400/40 hover:border-cyan-400 transition"
            >
              <Share2 className="h-3.5 w-3.5" /> Share Kit
            </button>
            <button onClick={onClose} data-testid="trial-modal-close" className="p-2 rounded-lg hover:bg-white/10">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="shrink-0 flex items-center gap-1 px-3 lg:px-6 py-3 border-b border-white/10 overflow-x-auto sidebar-scroll">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`trial-tab-${id}`}
              className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs lg:text-sm font-semibold transition ${
                tab === id ? "bg-white/10 text-white" : "text-white/55 hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 sidebar-scroll">
          {tab === "brand" &&     <BrandPanel kit={kit} businessName={input.name} trialId={trialId} onLogoGenerated={onLogoGenerated} />}
          {tab === "website" &&   <WebsitePanel kit={kit} businessName={input.name} />}
          {tab === "facebook" &&  <FacebookPanel kit={kit} businessName={input.name} />}
          {tab === "instagram" && <InstagramPanel kit={kit} businessName={input.name} />}
          {tab === "linkedin" &&  <LinkedInPanel kit={kit} businessName={input.name} />}
          {tab === "x" &&         <XPanel kit={kit} businessName={input.name} />}
          {tab === "gmb" &&       <GMBPanel kit={kit} businessName={input.name} />}
        </div>

        {/* Footer CTA */}
        <div className="shrink-0 border-t border-fuchsia-500/30 bg-gradient-to-r from-fuchsia-500/20 via-violet-600/15 to-cyan-500/15 px-6 py-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="font-heading text-lg lg:text-xl font-black">Like what you see?</div>
              <div className="text-sm text-white/70 mt-0.5">
                This is just a sneak peek — sign up to get your full website, daily posts, ads, and AI automation.
              </div>
            </div>
            <Link
              to={ctaHref}
              onClick={onClose}
              data-testid="trial-cta-signup"
              className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-base text-white bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:shadow-2xl hover:shadow-fuchsia-500/40 transition"
            >
              Build Your Brand <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
          <div className="mt-3 text-center text-xs text-white/40">
            <Link to={pricingHref} onClick={onClose} className="underline hover:text-white">See pricing plans</Link>
            {" · "}
            <span>Plans starting ₹999/month</span>
          </div>
        </div>
      </motion.div>

      {shareOpen && trialId && (
        <ShareKitDialog
          trialId={trialId}
          businessName={input.name}
          onClose={() => setShareOpen(false)}
        />
      )}
    </motion.div>
  );
}

/* =====================================================================
 * Share Kit Dialog — Email gated, then social share buttons
 * ===================================================================== */
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

function ShareKitDialog({ trialId, businessName, onClose }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const validate = () => EMAIL_RE.test(email.trim());

  const submit = async () => {
    if (!validate()) {
      toast.error("Please enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/trial/instant-brand/${trialId}/share`, {
        email: email.trim(),
      });
      const url = `${window.location.origin}${data.share_path}`;
      setShareUrl(url);
      toast.success("Share link unlocked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unlock share link");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const shareTo = (platform) => {
    const text = `Check out the brand kit AI just made for "${businessName}" — built with MakeYourBrand.IN`;
    const enc = encodeURIComponent;
    const urls = {
      whatsapp: `https://api.whatsapp.com/send?text=${enc(text + " " + shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
      email: `mailto:?subject=${enc(`My new brand kit for ${businessName}`)}&body=${enc(text + "\n\n" + shareUrl)}`,
    };
    window.open(urls[platform], "_blank", "noopener,noreferrer,width=600,height=600");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={onClose}
      data-testid="share-kit-dialog"
    >
      <motion.div
        initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 16 }}
        className="relative w-full max-w-md bg-[#0c0626] border border-cyan-500/40 rounded-2xl p-6 shadow-2xl shadow-cyan-500/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg lg:text-xl font-black inline-flex items-center gap-2">
            <Share2 className="h-5 w-5 text-cyan-400" /> Share your brand kit
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </div>

        {!shareUrl ? (
          <>
            <p className="text-sm text-white/60 mb-5">
              Enter your email to unlock a shareable link. We&apos;ll only use it to send you brand updates and offers — no spam.
            </p>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold inline-flex items-center gap-1.5">
                <Mail className="h-3 w-3" /> Your email
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="you@business.com"
                data-testid="share-email-input"
                autoFocus
                className="mt-2 w-full bg-black/40 border-2 border-white/10 focus:border-cyan-500/60 outline-none rounded-xl px-4 py-3 text-base font-medium transition"
              />
            </label>
            <button
              onClick={submit}
              disabled={busy}
              data-testid="share-email-submit"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-base text-white bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:opacity-95 transition disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Share2 className="h-5 w-5" />}
              Unlock Share Link
            </button>
            <p className="mt-3 text-center text-xs text-white/40">
              By submitting you agree to receive brand updates from MakeYourBrand.IN.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-white/70 mb-4">
              Your shareable kit is live. Choose a platform:
            </p>
            <div className="grid grid-cols-5 gap-2 mb-5" data-testid="share-platforms">
              <ShareIcon label="WhatsApp" onClick={() => shareTo("whatsapp")} bg="#25D366">
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white">
                  <path d="M17.5 14.4c-.3-.1-1.6-.8-1.8-.9-.2-.1-.4-.1-.6.1-.2.3-.7.9-.8 1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.4.1-.6.1-.1.3-.3.4-.5.1-.1.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.6-1.5-.9-2-.2-.5-.5-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2 0 1.3.9 2.6 1.1 2.7.1.2 1.9 2.9 4.6 4 .6.3 1.1.4 1.5.5.6.2 1.2.2 1.7.1.5-.1 1.6-.7 1.9-1.3.2-.6.2-1.1.2-1.3-.1-.1-.3-.2-.5-.3z"/>
                  <path d="M20.5 3.5C18.3 1.2 15.3 0 12.1 0 5.6 0 .3 5.3.3 11.8c0 2.1.5 4.1 1.6 5.9L0 24l6.5-1.7c1.7.9 3.6 1.4 5.6 1.4 6.5 0 11.8-5.3 11.8-11.8 0-3.2-1.2-6.1-3.4-8.4zM12.1 21.7c-1.8 0-3.5-.5-5-1.4l-.4-.2-3.7 1 1-3.6-.2-.4c-1-1.6-1.5-3.4-1.5-5.3 0-5.4 4.4-9.8 9.8-9.8 2.6 0 5.1 1 6.9 2.9 1.8 1.8 2.9 4.3 2.9 6.9 0 5.4-4.4 9.9-9.8 9.9z"/>
                </svg>
              </ShareIcon>
              <ShareIcon label="X" onClick={() => shareTo("twitter")} bg="#000">
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white">
                  <path d="M16.7 6h2L13.2 12 19.7 18h-4.6l-3.5-4.2L7.7 18H5.5l5.7-6.4L5 6h4.7l3.2 3.8z"/>
                </svg>
              </ShareIcon>
              <ShareIcon label="LinkedIn" onClick={() => shareTo("linkedin")} bg="#0A66C2">
                <Linkedin className="h-5 w-5 text-white" />
              </ShareIcon>
              <ShareIcon label="Facebook" onClick={() => shareTo("facebook")} bg="#1877F2">
                <Facebook className="h-5 w-5 text-white" />
              </ShareIcon>
              <ShareIcon label="Email" onClick={() => shareTo("email")} bg="#6b7280">
                <Mail className="h-5 w-5 text-white" />
              </ShareIcon>
            </div>
            <div className="bg-black/40 border border-white/10 rounded-xl p-3 flex items-center gap-2">
              <input
                readOnly
                value={shareUrl}
                data-testid="share-url-input"
                className="flex-1 bg-transparent outline-none text-xs text-white/80 font-mono truncate"
              />
              <button
                onClick={copy}
                data-testid="share-copy-button"
                className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold bg-white/10 hover:bg-white/20 transition"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

function ShareIcon({ label, onClick, bg, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={`share-to-${label.toLowerCase()}`}
      className="flex flex-col items-center gap-1.5 group"
    >
      <div
        className="h-12 w-12 rounded-xl grid place-items-center shadow-lg group-hover:scale-110 transition-transform"
        style={{ background: bg }}
      >
        {children}
      </div>
      <span className="text-[10px] text-white/60 group-hover:text-white">{label}</span>
    </button>
  );
}

/* =====================================================================
 * Panel renderers
 * ===================================================================== */
function BrandPanel({ kit, businessName, trialId, onLogoGenerated }) {
  const b = kit.brand || {};
  const [busy, setBusy] = useState(false);
  const generateRealLogo = async () => {
    if (!trialId) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/trial/instant-brand/logo`, { trial_id: trialId });
      onLogoGenerated?.(data.logo_image_b64);
      const remaining = typeof data.remaining_logo_quota === "number" ? data.remaining_logo_quota : null;
      toast.success(remaining !== null ? `Logo rendered · ${remaining} free logos left today` : "Logo rendered");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Logo generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Logo mockup OR real generated logo */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">
            {kit.logo_image_b64 ? "Your AI-Generated Logo" : "Logo Concept"}
          </div>
          {!kit.logo_image_b64 && (
            <button
              onClick={generateRealLogo}
              disabled={busy || !trialId}
              data-testid="trial-generate-real-logo"
              className="text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-full bg-gradient-to-r from-fuchsia-500/30 to-cyan-500/30 border border-fuchsia-400/40 hover:border-fuchsia-400 transition disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              Generate Real Logo
            </button>
          )}
        </div>
        {kit.logo_image_b64 ? (
          <div className="aspect-square rounded-2xl overflow-hidden bg-white border-2 border-fuchsia-500/30 shadow-2xl shadow-fuchsia-500/20">
            <img
              src={`data:image/png;base64,${kit.logo_image_b64}`}
              alt={`${businessName} logo`}
              className="w-full h-full object-contain"
              data-testid="trial-real-logo-img"
            />
          </div>
        ) : (
          <div
            className="aspect-[4/3] rounded-2xl flex flex-col items-center justify-center gap-3 border-2"
            style={{
              background: `linear-gradient(135deg, ${b.primary_color}25, ${b.accent_color || "#22d3ee"}15)`,
              borderColor: `${b.primary_color}40`,
            }}
          >
            <div
              className="h-20 w-20 rounded-2xl flex items-center justify-center shadow-2xl"
              style={{ background: `linear-gradient(135deg, ${b.primary_color}, ${b.accent_color || "#22d3ee"})` }}
            >
              <span className="font-heading font-black text-4xl" style={{ color: b.text_color || "#fff" }}>
                {businessName.trim().charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="text-center">
              <div className="font-heading font-black text-2xl tracking-tight">{businessName}</div>
              <div className="text-xs text-white/60 mt-1 italic">{kit.tagline}</div>
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-white/40 mt-1">
              Placeholder — click &ldquo;Generate Real Logo&rdquo; above
            </div>
          </div>
        )}
      </div>
      {/* Brand details */}
      <div className="space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">Tagline</div>
          <div className="glass rounded-xl p-4 italic text-white/85">&ldquo;{kit.tagline}&rdquo;</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">Color Palette</div>
          <div className="flex gap-2">
            {[b.primary_color, b.accent_color, b.text_color].filter(Boolean).map((c) => (
              <div key={c} className="flex-1 glass rounded-xl p-3 text-center">
                <div className="h-10 rounded-md mb-1.5" style={{ background: c }} />
                <div className="text-[10px] font-mono uppercase text-white/60">{c}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">Brand Voice</div>
          <div className="glass rounded-xl p-4 text-sm text-white/80">{b.voice}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">Logo Style</div>
          <div className="glass rounded-xl p-4 text-sm text-white/80">{b.logo_style}</div>
        </div>
      </div>
    </div>
  );
}

function WebsitePanel({ kit, businessName }) {
  const w = kit.website || {};
  const b = kit.brand || {};
  return (
    <div className="space-y-4">
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">Website Hero Preview</div>
      <div
        className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl"
        style={{ background: `linear-gradient(135deg, ${b.primary_color}, ${b.accent_color || "#1a0a3e"})` }}
      >
        {/* Fake browser chrome */}
        <div className="bg-black/30 px-4 py-2 flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full bg-red-500/70" />
          <span className="h-3 w-3 rounded-full bg-yellow-500/70" />
          <span className="h-3 w-3 rounded-full bg-green-500/70" />
          <span className="ml-3 text-[10px] text-white/60 font-mono">{businessName.toLowerCase().replace(/\s+/g, "")}.com</span>
        </div>
        <div className="p-8 lg:p-12">
          <div className="font-heading text-2xl lg:text-4xl font-black leading-tight text-white">
            {w.hero_headline}
          </div>
          <p className="mt-3 text-white/85 text-sm lg:text-base max-w-2xl">
            {w.hero_sub}
          </p>
          <div className="mt-6 flex gap-3 flex-wrap">
            <span className="px-5 py-2.5 rounded-lg bg-white text-[#0c0626] font-bold text-sm">{w.cta_primary}</span>
            <span className="px-5 py-2.5 rounded-lg border-2 border-white/40 text-white font-bold text-sm">{w.cta_secondary}</span>
          </div>
          {(w.features || []).length > 0 && (
            <div className="mt-8 grid grid-cols-3 gap-3">
              {(w.features || []).slice(0, 3).map((f, i) => (
                <div key={i} className="bg-white/10 rounded-lg p-3 text-xs lg:text-sm text-white/90">{f}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SocialEngagementBar({ likes = "1.2K", comments = "84", shares = "32" }) {
  return (
    <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-6 text-xs text-white/50">
      <span className="inline-flex items-center gap-1"><ThumbsUp className="h-3.5 w-3.5" /> {likes}</span>
      <span className="inline-flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> {comments}</span>
      <span className="inline-flex items-center gap-1"><Share2 className="h-3.5 w-3.5" /> {shares}</span>
    </div>
  );
}

function PostFrame({ brand, businessName, children, platform, color = "#1877F2" }) {
  return (
    <div className="bg-white text-[#0c0626] rounded-2xl shadow-2xl max-w-xl mx-auto overflow-hidden">
      <div className="flex items-center gap-3 p-4 border-b border-black/5">
        <div className="h-11 w-11 rounded-full grid place-items-center text-white font-black text-lg" style={{ background: brand.primary_color || color }}>
          {businessName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="font-bold text-sm">{businessName}</div>
          <div className="text-[11px] text-gray-500">Just now · {platform}</div>
        </div>
      </div>
      <div className="p-4">
        {children}
      </div>
    </div>
  );
}

function FacebookPanel({ kit, businessName }) {
  const fb = kit.facebook || {};
  return (
    <PostFrame brand={kit.brand} businessName={businessName} platform="Public" color="#1877F2">
      <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{fb.body}</p>
      <div className="mt-2 text-blue-700 text-sm">{(fb.hashtags || []).map((h) => `#${h.replace(/^#/, "")}`).join(" ")}</div>
      <SocialEngagementBar likes="3.2K" comments="284" shares="156" />
    </PostFrame>
  );
}

function InstagramPanel({ kit, businessName }) {
  const ig = kit.instagram || {};
  const b = kit.brand || {};
  return (
    <div className="max-w-md mx-auto bg-white text-[#0c0626] rounded-2xl shadow-2xl overflow-hidden">
      <div className="flex items-center gap-3 p-3 border-b border-black/5">
        <div className="h-9 w-9 rounded-full p-[2px]" style={{ background: "linear-gradient(135deg,#f9ce34,#ee2a7b,#6228d7)" }}>
          <div className="h-full w-full rounded-full grid place-items-center text-white font-black text-sm" style={{ background: b.primary_color }}>
            {businessName.charAt(0).toUpperCase()}
          </div>
        </div>
        <div className="flex-1 text-sm font-bold">{businessName.toLowerCase().replace(/\s+/g, "_")}</div>
      </div>
      <div
        className="aspect-square flex items-center justify-center text-white p-8 text-center"
        style={{ background: `linear-gradient(135deg, ${b.primary_color}, ${b.accent_color || "#7c3aed"})` }}
      >
        <div>
          <ImageIcon className="h-14 w-14 mx-auto opacity-60 mb-3" />
          <div className="font-heading font-black text-3xl">{businessName}</div>
          <div className="italic text-sm mt-2 opacity-90">{kit.tagline}</div>
        </div>
      </div>
      <div className="flex items-center gap-4 p-3">
        <Heart className="h-6 w-6" /> <MessageCircle className="h-6 w-6" /> <Share2 className="h-6 w-6" />
      </div>
      <div className="px-3 pb-4 text-sm">
        <span className="font-bold">{businessName.toLowerCase().replace(/\s+/g, "_")}</span>{" "}
        {ig.caption}
        <div className="mt-1 text-[#385898] text-xs">{(ig.hashtags || []).slice(0, 10).map((h) => `#${h.replace(/^#/, "")}`).join(" ")}</div>
      </div>
    </div>
  );
}

function LinkedInPanel({ kit, businessName }) {
  const li = kit.linkedin || {};
  return (
    <PostFrame brand={kit.brand} businessName={businessName} platform="Followers" color="#0A66C2">
      <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{li.post}</p>
      <div className="mt-2 text-[#0A66C2] text-sm">{(li.hashtags || []).map((h) => `#${h.replace(/^#/, "")}`).join(" ")}</div>
      <SocialEngagementBar likes="847" comments="62" shares="48" />
    </PostFrame>
  );
}

function XPanel({ kit, businessName }) {
  const x = kit.x_twitter || {};
  return (
    <div className="bg-white text-black rounded-2xl shadow-2xl max-w-xl mx-auto overflow-hidden">
      <div className="flex gap-3 p-4">
        <div className="h-12 w-12 rounded-full shrink-0 grid place-items-center text-white font-black text-lg" style={{ background: kit.brand?.primary_color }}>
          {businessName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm">
            <span className="font-bold">{businessName}</span>
            <span className="text-gray-500"> @{businessName.toLowerCase().replace(/\s+/g, "")} · now</span>
          </div>
          <p className="mt-1 text-[15px] leading-relaxed">{x.tweet}</p>
          <div className="mt-2 text-[#1da1f2] text-sm">{(x.hashtags || []).map((h) => `#${h.replace(/^#/, "")}`).join(" ")}</div>
          <SocialEngagementBar likes="2.1K" comments="142" shares="389" />
        </div>
      </div>
    </div>
  );
}

function GMBPanel({ kit, businessName }) {
  const g = kit.google_business || {};
  const b = kit.brand || {};
  return (
    <div className="bg-white text-[#0c0626] rounded-2xl shadow-2xl max-w-xl mx-auto overflow-hidden">
      <div className="flex items-center gap-3 p-4 border-b border-black/5">
        <div className="h-10 w-10 rounded-full grid place-items-center text-white font-black" style={{ background: b.primary_color }}>
          {businessName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="font-bold text-sm">{businessName}</div>
          <div className="text-[11px] text-gray-500 inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> Google Business Profile</div>
        </div>
      </div>
      <div
        className="aspect-[2/1] grid place-items-center text-white p-6"
        style={{ background: `linear-gradient(135deg, ${b.primary_color}, ${b.accent_color || "#5b21b6"})` }}
      >
        <div className="text-center">
          <ShoppingBag className="h-10 w-10 mx-auto mb-2 opacity-80" />
          <div className="font-heading font-black text-xl">{kit.tagline}</div>
        </div>
      </div>
      <div className="p-4">
        <p className="text-sm leading-relaxed">{g.post}</p>
        <button className="mt-3 px-4 py-2 rounded-full text-sm font-bold border-2" style={{ borderColor: b.primary_color, color: b.primary_color }}>
          {g.cta_label || "Learn More"} →
        </button>
      </div>
    </div>
  );
}
